/**
 * Renaming, deleting and copying a table.
 *
 * The tables live nested on the database application in `qk.applications`,
 * because that is the shape the endpoint answers in and the shape the sidebar
 * renders from -- so these reach for the same updaters the realtime layer uses
 * rather than keeping a second copy of the tree.
 *
 * There is no *creating* a table here, and that is deliberate rather than
 * missing: tables come from the API and the MCP tools in this fork, and there
 * has never been an interface path for making one.
 */
import {
  mutationOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { Application, Job } from '@/api/types'
import { pushJob } from '@/features/job/queries'
import { removeTable, updateTable } from '@/features/realtime/handlers/tables'

interface TableTarget {
  workspaceId: number
  databaseId: number
  tableId: number
}

function optimistically(
  queryClient: QueryClient,
  workspaceId: number,
  update: (applications: Application[]) => Application[],
): Application[] | undefined {
  const key = qk.applications(workspaceId)
  const previous = queryClient.getQueryData<Application[]>(key)
  if (previous !== undefined) {
    queryClient.setQueryData<Application[]>(key, update(previous))
  }
  return previous
}

function rollback(
  queryClient: QueryClient,
  workspaceId: number,
  previous: Application[] | undefined,
): void {
  if (previous !== undefined) {
    queryClient.setQueryData(qk.applications(workspaceId), previous)
  }
}

export function updateTableMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({ tableId, name }: TableTarget & { name: string }) =>
      unwrap(
        getApi().PATCH('/api/database/tables/{table_id}/', {
          params: { path: { table_id: tableId } },
          body: { name },
        }),
      ),
    // Optimistic, and rolled back by restoring the snapshot rather than by
    // inverting the change: a realtime event can land between the two.
    onMutate: ({ workspaceId, databaseId, tableId, name }) => ({
      previous: optimistically(queryClient, workspaceId, (applications) =>
        updateTable(applications, databaseId, tableId, { name }),
      ),
    }),
    onError: (_error, { workspaceId }, context) =>
      rollback(queryClient, workspaceId, context?.previous),
  })
}

export function useUpdateTable() {
  return useMutation(updateTableMutation(useQueryClient()))
}

/**
 * Deleting a table, which trashes it rather than destroying it.
 *
 * Everything keyed under the table is dropped afterwards -- its fields, views,
 * webhooks and the rest. Invalidating them would refetch a table that is no
 * longer there; removing them leaves nothing to refetch.
 */
export function deleteTableMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({ tableId }: TableTarget) =>
      unwrap(
        getApi().DELETE('/api/database/tables/{table_id}/', {
          params: { path: { table_id: tableId } },
        }),
      ),
    onMutate: ({ workspaceId, databaseId, tableId }) => ({
      previous: optimistically(queryClient, workspaceId, (applications) =>
        removeTable(applications, databaseId, tableId),
      ),
    }),
    onError: (_error, { workspaceId }, context) =>
      rollback(queryClient, workspaceId, context?.previous),
    onSuccess: (_data, { tableId }) => {
      queryClient.removeQueries({ queryKey: qk.table(tableId) })
    },
  })
}

export function useDeleteTable() {
  return useMutation(deleteTableMutation(useQueryClient()))
}

/**
 * Copying a table, which is a job because it copies every row.
 *
 * Nothing optimistic and nothing waited for: the copy arrives over the socket
 * as a `table_created`, the way a duplicated application does, and what the job
 * is for is saying how far along it is while it runs.
 */
export function duplicateTableMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (tableId: number): Promise<Job> =>
      unwrap(
        getApi().POST('/api/database/tables/{table_id}/duplicate/async/', {
          params: { path: { table_id: tableId } },
        }),
      ) as Promise<Job>,
    onSuccess: (job) => {
      pushJob(queryClient, job)
    },
  })
}

export function useDuplicateTable() {
  return useMutation(duplicateTableMutation(useQueryClient()))
}
